import { UserRole } from '@erikdakoda/database/enums';

import type { AuthUser } from '../types';

/**
 * Ownership fields needed to decide UI write access for NamedItem / OwnedItem /
 * ConnectionItem delegate rows.
 *
 * `private` exists on NamedItem and OwnedItem; omit it (or pass `false`/`null`) for
 * ConnectionItem so space members are treated as writable when they share the space.
 */
export type DelegateItemForWriteCheck = {
  readonly ownerId: string;
  readonly spaceId: string | null;
  readonly private?: boolean | null;
};

/**
 * Session fields used by {@link isDelegateItemWritable}.
 *
 * Prefer the authenticated user from `useCurrentUser` (`AuthUser`), which includes `spaces`.
 */
export type UserForWriteCheck = {
  readonly id: string;
  readonly role: string;
  readonly spaces?: readonly { readonly spaceId: string }[] | null;
};

/**
 * Returns whether the current user may write a NamedItem / OwnedItem / ConnectionItem row
 * in the UI.
 *
 * Approximation of Base + ownership update policy for editors (ZenStack v3 has no `check()`
 * API). Writable when the user is an app ADMIN, the row owner, or a member of the row's space
 * when the row is not private. Does not model ConnectionItem scope denies or field-level
 * `@deny` rules — the server remains authoritative.
 * @param item The delegate row (or a pick of its ownership fields), or null/undefined.
 * @param user The current user (or a pick of id/role/spaces), or null/undefined.
 * @returns True when the UI should treat the row as writable.
 */
export function isDelegateItemWritable(
  item: DelegateItemForWriteCheck | null | undefined,
  user: UserForWriteCheck | null | undefined,
): boolean {
  if (!item || !user) {
    return false;
  }

  if (user.role === UserRole.ADMIN) {
    return true;
  }

  if (item.ownerId === user.id) {
    return true;
  }

  if (item.private === true) {
    return false;
  }

  if (!item.spaceId || !user.spaces) {
    return false;
  }

  return user.spaces.some((spaceUser) => spaceUser.spaceId === item.spaceId);
}

export const ownedItemFieldsToOmitForClone = [
  'id',
  'updatedAt',
  'createdAt',
  'trashedAt',
  'itemType',
];

export const namedItemFieldsToOmitForClone = [
  'id',
  'updatedAt',
  'createdAt',
  'trashedAt',
  'publishedAt',
  'itemType',
  'slug',
];

export type UserForDisplayName = Pick<AuthUser, 'givenName' | 'familyName' | 'nickName' | 'email'>;

/**
 * Display name for quiz results and similar labels: nickName, then given + family, then email local part.
 */
export function getUserDisplayName(user: UserForDisplayName): string {
  const nick = user.nickName?.trim();
  if (nick) {
    return nick;
  }
  const fullName = `${user.givenName} ${user.familyName ?? ''}`.trim();
  if (fullName) {
    return fullName;
  }
  return user.email.split('@')[0] ?? user.email;
}

export function getUserName(
  user: Partial<Pick<AuthUser, 'givenName' | 'familyName' | 'email'>> | null,
): string | undefined {
  if (!user) return undefined;

  const userName = user?.givenName
    ? `${user.givenName} ${user.familyName || ''}`.trim()
    : user?.email?.split('@')[0];
  return userName;
}

export function getUserInitials(
  user: Partial<Pick<AuthUser, 'givenName' | 'familyName' | 'email'>> | null,
): string | undefined {
  if (!user) return undefined;

  const userName = getUserName(user);
  if (!userName) return undefined;
  const parts = userName.split(' ');

  let initials = '';
  if (parts[0]) {
    initials += parts[0].charAt(0).toUpperCase();
  }
  if (parts.length >= 2) {
    initials += parts[parts.length - 1]?.charAt(0).toUpperCase();
  }
  return initials;
}

export function identifyUser(
  user: Partial<Pick<AuthUser, 'id' | 'email'>> | null,
): string | undefined {
  if (!user) return undefined;

  const identification = `${user?.email} (${user?.id})`;
  return identification;
}

export function getUserAvatarId(user?: Pick<AuthUser, 'image'> | null): string | undefined {
  if (!user) return undefined;
  return user.image || undefined;
}

export function getUserProfileUrl(user?: Pick<AuthUser, 'id'> | null): string | undefined {
  if (!user) return undefined;
  return '/profile/' + user.id;
}
